import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { API_CONFIG, API_ENDPOINTS } from '@/shared/config/api';
import apiService from '@/shared/services/api';
import logger from '@/shared/utils/logger';
import { AUTH_ERROR_KEY } from '@/auth/utils/authErrorMessages';

export default function Callback() {
  const navigate = useNavigate();
  const {
    error,
    isLoading,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  useEffect(() => {
    let cancelled = false;

    const redirectHome = (message?: string, forceLogout = false) => {
      apiService.clearAuthTokens();
      if (message) {
        sessionStorage.setItem(AUTH_ERROR_KEY, message);
      } else {
        sessionStorage.removeItem(AUTH_ERROR_KEY);
      }
      if (forceLogout) {
        logout({
          logoutParams: {
            returnTo: window.location.origin + '/auth-error',
          },
        });
        return;
      }
      if (!cancelled) {
        navigate('/auth-error', { replace: true });
      }
    };

    if (error) {
      logger.error('Auth0 Error:', error);
      redirectHome(error.message, true);
      return () => {
        cancelled = true;
      };
    }

    if (isLoading) {
      return () => {
        cancelled = true;
      };
    }

    const handleToken = async () => {
      try {
        const auth0Token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_CONFIG.baseURL}${API_ENDPOINTS.auth.login}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${auth0Token}`,
            },
          }
        );

        if (!response.ok) {
          let errorMessage = `API responded with status ${response.status}`;

          try {
            const errorBody = await response.json();
            errorMessage = errorBody?.error?.message ?? errorMessage;
          } catch {
            // Ignore JSON parsing errors for non-JSON responses
          }

          if (response.status === 401 || response.status === 403) {
            logger.warn('Acceso denegado para el usuario actual:', errorMessage);
            redirectHome(errorMessage, true);
            return;
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();

        if (!data?.success) {
          throw new Error('API login failed');
        }

        const authToken = response.headers.get('x-auth-token');
        const refreshToken = response.headers.get('x-refresh-token');

        if (!authToken || !refreshToken) {
          logger.error('Missing authentication tokens in response headers');
          redirectHome('No se pudo completar la autenticación', true);
          return;
        }

        apiService.setAuthTokens(authToken, refreshToken);

        // Store user data with roles if available
        if (data.data?.user) {
          localStorage.setItem('user_data', JSON.stringify(data.data.user));
        }

        if (!cancelled) {
          navigate('/');
        }
      } catch (tokenExchangeError) {
        logger.error('Error durante el intercambio de tokens:', tokenExchangeError);

        const message =
          tokenExchangeError instanceof Error
            ? tokenExchangeError.message
            : 'Error during token exchange';

        // Los errores de Auth0 traen un campo `error` de tipo string (ej.: 'login_required')
        const isAuth0Error =
          tokenExchangeError instanceof Error &&
          typeof (tokenExchangeError as { error?: unknown }).error === 'string';

        redirectHome(message, isAuth0Error);
      }
    };

    handleToken();

    return () => {
      cancelled = true;
    };
    // `getAccessTokenSilently`, `logout` y `navigate` quedan fuera a propósito: el
    // intercambio de tokens contra el backend tiene que dispararse una sola vez, cuando
    // Auth0 termina de cargar. `navigate` cambia con cada navegación, así que incluirlo
    // volvería a hacer el POST /auth/login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg">
        <LoadingSpinner size="lg" className="mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Iniciando Sesión...
        </h2>
        <p className="text-gray-600">
          Por favor espera mientras configuramos tu sesión.
        </p>
      </div>
    </div>
  );
}
